import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, Form, Input, Button, Typography, Result } from 'antd';
import { MailOutlined } from '@ant-design/icons';
import { authService } from '../../services/authService';

const { Title, Paragraph } = Typography;

/**
 * Public page: an operator requests a password-reset email. The admin-portal is
 * the platform operator console, so the workspace is fixed to 'platform'. We
 * always show the same "check your email" result (never reveal whether an
 * account exists for the address).
 */
const ForgotPasswordPage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const onFinish = async (values: { email: string }) => {
    setLoading(true);
    try {
      await authService.forgotPassword(values.email, 'platform');
    } catch {
      // Intentionally ignored — do not leak account existence.
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <Card className="shadow-lg" style={{ maxWidth: 420, margin: '0 auto' }}>
        <Result
          status="success"
          title="Check your email"
          subTitle="If an account exists for that address, we've sent a link to reset your password. The link expires shortly."
          extra={
            <Link to="/login">
              <Button type="primary">Back to sign in</Button>
            </Link>
          }
        />
      </Card>
    );
  }

  return (
    <Card className="shadow-lg" style={{ maxWidth: 420, margin: '0 auto' }}>
      <div className="text-center mb-6">
        <Title level={3} style={{ marginBottom: 4 }}>
          Reset your password
        </Title>
        <Paragraph type="secondary">Enter your account email and we'll send you a reset link.</Paragraph>
      </div>

      <Form layout="vertical" onFinish={onFinish} requiredMark={false}>
        <Form.Item
          name="email"
          label="Email"
          rules={[{ required: true, type: 'email', message: 'Enter a valid email' }]}
        >
          <Input prefix={<MailOutlined />} placeholder="you@company.com" autoComplete="username" />
        </Form.Item>

        <Form.Item style={{ marginBottom: 8 }}>
          <Button type="primary" htmlType="submit" block loading={loading}>
            Send reset link
          </Button>
        </Form.Item>
      </Form>

      <div className="text-center mt-2">
        <Link to="/login">Back to sign in</Link>
      </div>
    </Card>
  );
};

export default ForgotPasswordPage;
